import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Navigation from './components/layout/Navigation';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RustPage from './pages/RustPage';
import UserPanel from './pages/UserPanel';
import LoadingScreen from './components/layout/LoadingScreen';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Scroll restoration component
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Disable browser's scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    // Force scroll to top
    window.scrollTo(0, 0);

    // Reset any scrollable containers
    const scrollableElements = document.querySelectorAll('.overflow-y-auto, .overflow-auto');
    scrollableElements.forEach(element => {
      (element as HTMLElement).scrollTop = 0;
    });

    // Cleanup function
    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, [pathname]);

  return null;
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const { isAuthenticated, logout } = useAuth();

  // Preload essential assets like images
  useEffect(() => {
    const preloadImage = (src: string) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = resolve;
        img.onerror = reject;
      });
    };

    // We could preload more assets here if needed
    Promise.all([
      preloadImage('/images/logo.png'),
      preloadImage('/images/menu_recoil.png'),
      preloadImage('/images/menu_misc.png'),
      preloadImage('/images/menu_settings.png'),
      // Add other critical images here
    ]).catch(err => console.log('Error preloading images:', err));
  }, []);

  const handleLoadComplete = () => {
    setIsLoading(false);
    // Set a flag to completely remove the loading screen from DOM after fade out
    setTimeout(() => {
      setFinishedLoading(true);
    }, 1000);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-gray-950">
      <ScrollToTop />
      <Navigation isAuthenticated={isAuthenticated} onLogout={handleLogout} />
      <div className="pt-16"> {/* Add padding to account for fixed navbar */}
        {!finishedLoading && (
          <LoadingScreen onLoadComplete={handleLoadComplete} />
        )}
        
        <div 
          className={`h-full overflow-auto transition-opacity duration-1000 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          style={{ transitionDelay: isLoading ? '0ms' : '500ms' }}
        >
          <Routes>
            <Route path="/" element={<HomePage navigateToLogin={() => navigate('/login')} />} />
            <Route path="/login" element={
              isAuthenticated 
                ? <Navigate to="/user-panel" replace /> 
                : <LoginPage onLogin={() => navigate('/user-panel')} />
            } />
            <Route path="/products/rust" element={<RustPage navigateToLogin={() => navigate('/login')} />} />
            <Route path="/user-panel" element={
              isAuthenticated 
                ? <UserPanel onLogout={handleLogout} />
                : <Navigate to="/login" replace />
            } />
            {/* Catch-all route that redirects to home page */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App; 